// bottAvatar — single source of truth for the per-user spectator avatar.
//
// We render each spectator as a DiceBear "bottts" robot whose two colors
// (background ring + body) are pinned to a curated 15-color matte palette
// that lives on-brand with the boothfm room. Identity is split into two
// fields stored on `agents`:
//
//   - avatar_seed:           a short stable string. DiceBear hashes it
//                            into a deterministic robot face. Changing
//                            the seed swaps the face (intentional, but
//                            rare — we keep seeds stable across logins so
//                            friends recognize each other by their bot).
//   - avatar_palette_index:  index into PALETTE. Drives the background.
//                            The body color is sampled deterministically
//                            by DiceBear from `PALETTE \ {bg}` so body
//                            and background can never match.
//
// The palette is intentionally matte and warm — see avatar-spike.html for
// the rationale (cool counterweights balance the otherwise warm-heavy
// console aesthetic). Order is not random: rotating to the next index
// during a re-roll deliberately walks to a perceptually-different color
// rather than landing on a near-neighbor.

export interface PaletteColor {
  /** Human-readable name surfaced in settings UI. */
  name: string;
  /** Hex without leading `#` — DiceBear's query param format. */
  hex: string;
}

export const PALETTE: readonly PaletteColor[] = [
  { name: 'magenta',    hex: 'E94B92' },
  { name: 'violet',     hex: '7C3AED' },
  { name: 'plum',       hex: '5B4E7A' },
  { name: 'mauve',      hex: '9B6B8E' },
  { name: 'indigo',     hex: '3B4476' },
  { name: 'teal',       hex: '4A8A8E' },
  { name: 'sage',       hex: '8A9E7A' },
  { name: 'olive',      hex: '7A8050' },
  { name: 'mustard',    hex: 'C9A646' },
  { name: 'amber',      hex: 'E0A459' },
  { name: 'tungsten',   hex: 'FFD68F' },
  { name: 'clay',       hex: 'B87D3A' },
  { name: 'coral',      hex: 'D88471' },
  { name: 'brick',      hex: 'B84B3A' },
  { name: 'dusty rose', hex: 'C98B8B' },
];

export const PALETTE_SIZE = PALETTE.length;

const DICEBEAR_BASE = 'https://api.dicebear.com/9.x/bottts/svg';

/** Clamp an arbitrary integer into a valid palette index. Used everywhere
 *  we hand a number back to the renderer; defends against DB rows from
 *  before the palette grew or shrank. */
export function normalizePaletteIndex(i: number): number {
  if (!Number.isFinite(i)) return 0;
  const n = Math.trunc(i);
  return ((n % PALETTE_SIZE) + PALETTE_SIZE) % PALETTE_SIZE;
}

/** Build the DiceBear URL for a spectator bot. Background is fixed to the
 *  user's palette color; body is sampled by DiceBear from the rest of the
 *  palette so body ≠ background is guaranteed. */
export function bottAvatarUrl(seed: string, paletteIndex: number): string {
  const i = normalizePaletteIndex(paletteIndex);
  const bg = PALETTE[i].hex;
  // Body candidates = palette minus the background color. Passing this
  // CSV to `baseColor` lets DiceBear pick deterministically from `seed`
  // while making a body=bg collision physically impossible.
  const bodyCandidates = PALETTE
    .filter((c) => c.hex !== bg)
    .map((c) => c.hex)
    .join(',');
  const params = new URLSearchParams({
    seed,
    backgroundColor: bg,
    baseColor: bodyCandidates,
  });
  return `${DICEBEAR_BASE}?${params.toString()}`;
}

/** A new random seed for a brand-new user. 8 chars of base36 is plenty:
 *  the seed is only ever used as the input to DiceBear's hash, so we just
 *  need enough entropy that two users almost never collide on the same
 *  face (collision is harmless — they'd still differ by color). */
export function randomAvatarSeed(): string {
  if (typeof crypto !== 'undefined' && 'getRandomValues' in crypto) {
    const buf = new Uint8Array(6);
    crypto.getRandomValues(buf);
    return Array.from(buf, (b) => b.toString(36).padStart(2, '0')).join('').slice(0, 8);
  }
  return Math.random().toString(36).slice(2, 10);
}

/** Deterministically derive a palette index from any stable string (e.g.
 *  a user id). Used by the migration to backfill existing rows and by the
 *  signup default so a brand-new agent has a stable color before they
 *  re-roll. djb2-style — matches the hash family used elsewhere in the
 *  codebase (see PresenceTokens.userHash). */
export function hashToPaletteIndex(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) {
    h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  }
  return normalizePaletteIndex(Math.abs(h));
}

/** Walk to the next palette color. Re-roll wraps around so a user can
 *  always step forward without hitting a dead end. */
export function nextPaletteIndex(current: number): number {
  return normalizePaletteIndex(current + 1);
}
